import type { Control, FieldError, FieldValues, Path } from 'react-hook-form'
import { Controller } from 'react-hook-form'

import { Button } from 'src/shared/components/ui/buttons/button'
import { cn } from 'src/shared/utils/cn'

import BusinessFormErrorMessage from './business-form-error-message'

interface BusinessButtonsFormFieldProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  options: { label: string; value?: string; subtitle?: string }[]
  onPress?: (label: string) => void
}

export default function BusinessButtonsFormField<T extends FieldValues>({
  control,
  name,
  options,
  onPress,
}: BusinessButtonsFormFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, formState: { errors } }) => (
        <div className="relative flex flex-col items-center gap-4">
          {options.map((option) => {
            const isSelected = value === option.value
            return (
              <Button
                key={option.value}
                type="button"
                variant="default"
                className={cn(
                  'flex h-[100px] w-full max-w-[500px] flex-row items-center justify-between rounded-[20px] bg-accent px-[30px] py-[17px] text-primary transition-all duration-300 hover:shadow-[0_1px_16px_1px_rgba(27,48,41,0.15)] active:shadow-[0_1px_16px_1px_rgba(27,48,41,0.15)]',
                  isSelected && 'bg-primary text-white',
                )}
                onClick={() => {
                  onChange(option.value)
                  onPress?.(option.label)
                }}
              >
                <div className="flex flex-col text-left text-[20px] font-semibold transition-none">
                  {option.label}
                  {option.subtitle && (
                    <div
                      className={cn('text-[16px] font-normal text-muted', isSelected && 'text-white text-opacity-80')}
                    >
                      {option.subtitle}
                    </div>
                  )}
                </div>
              </Button>
            )
          })}
          {errors[name] && (
            <div className="absolute bottom-[-40px]">
              <BusinessFormErrorMessage error={errors[name] as FieldError} />
            </div>
          )}
        </div>
      )}
    />
  )
}
